/**
 * Permanent versus ephemeral Odoo branches: the one place that tells them
 * apart, ranks them in tiers (production, staging, development, ephemeral),
 * orders them, and says how old and how idle an ephemeral one is.
 * Every screen that shows a branch — sidebar, home page, environments card,
 * branch page and its switcher — reads these functions, through
 * `BranchLabel`, so the marker cannot mean two things on two screens.
 *
 * Ephemeral means "the branch name starts with `dev-`": the
 * `ephemeralBranchPrefix` the `odoo-project` template (platform-catalog)
 * passes to the appenv template, whose ApplicationSet
 * (`applicationset-ephemeral.yaml`) deploys every branch matching `^dev-`.
 * The permanent branches (dev, preprod, prod) do not match. A project
 * scaffolded with another prefix would read as having permanent branches
 * only.
 *
 * Plain functions, no hook and no clock: the current time is an argument, so
 * the tests pin it.
 */

/**
 * The `ephemeralBranchPrefix` of the `odoo-project` template. The backend's
 * branch routes hold the same constant
 * (`plugins/waadoo-core-backend/src/plugins/waadooProjects/ephemeralBranch.ts`).
 */
export const EPHEMERAL_BRANCH_PREFIX = 'dev-';

/**
 * From this many days without a new deployment, an ephemeral environment is
 * flagged « inactive ? » — a question, not a verdict: the reader decides.
 */
export const INACTIVE_AFTER_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The permanent branch that is the production: `prod`, the name the
 * `odoo-project` template (platform-catalog) gives it among its default
 * `permanentBranches` (dev, preprod, prod). A project that renamed it reads
 * as having permanent branches only — the same limit as the prefix above.
 */
export const PRODUCTION_BRANCH = 'prod';

/**
 * The permanent branch that is the integration one: `dev`, the name the
 * `odoo-project` template (platform-catalog) gives it among its default
 * `permanentBranches` (dev, preprod, prod) — the branch the ephemeral ones
 * start from. A project that renamed it reads its integration branch as a
 * staging one, the same limit as {@link PRODUCTION_BRANCH}.
 */
export const DEVELOPMENT_BRANCH = 'dev';

export function isEphemeralBranch(branch: string): boolean {
  return branch.startsWith(EPHEMERAL_BRANCH_PREFIX);
}

/**
 * What a branch is, for its kind icon (`BranchLabel`): the production, another
 * permanent branch (dev, preprod…), or an ephemeral one. The order of the
 * lists and the tier colours are {@link branchTier}'s, which also tells the
 * integration branch from the staging ones.
 */
export type BranchKind = 'production' | 'permanent' | 'ephemeral';

export function branchKind(branch: string): BranchKind {
  if (isEphemeralBranch(branch)) {
    return 'ephemeral';
  }
  return branch === PRODUCTION_BRANCH ? 'production' : 'permanent';
}

/**
 * The tier of a branch, most critical first: the production (`prod`), a
 * staging branch (any other permanent one: preprod, main, staging…), the
 * integration branch (`dev`), an ephemeral branch (`dev-…`). It gives the
 * order of every list ({@link compareBranches}) and the tier colour
 * (`--waadoo-tier-*`, styles.css; `EnvironmentTag` and its reduced form
 * `BranchTierDot`, BranchLabel.tsx).
 */
export type BranchTier = 'production' | 'staging' | 'development' | 'ephemeral';

/** The tiers in the order of the lists, most critical first. */
export const BRANCH_TIERS: readonly BranchTier[] = [
  'production',
  'staging',
  'development',
  'ephemeral',
];

export function branchTier(branch: string): BranchTier {
  if (isEphemeralBranch(branch)) {
    return 'ephemeral';
  }
  if (branch === PRODUCTION_BRANCH) {
    return 'production';
  }
  return branch === DEVELOPMENT_BRANCH ? 'development' : 'staging';
}

/**
 * The order every list of branches follows: by tier ({@link branchTier}:
 * prod, then the staging branches, then dev, then the ephemeral ones), each
 * tier by a plain string comparison. Not `localeCompare`, whose collation
 * depends on the reader's locale — the order must be the same for everyone
 * and in every test run.
 */
export function compareBranches(a: string, b: string): number {
  const byTier =
    BRANCH_TIERS.indexOf(branchTier(a)) - BRANCH_TIERS.indexOf(branchTier(b));
  if (byTier !== 0) {
    return byTier;
  }
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

/**
 * The two dates an environment's timeline is read from, as `toEnvironment`
 * (argoApp.ts) extracts them from the ArgoCD Application. Both are ISO
 * timestamps; both are optional because the payload is read verbatim.
 */
export interface EnvironmentTiming {
  /** `metadata.creationTimestamp` of the Application. */
  createdAt?: string;
  /**
   * When the revision deployed now was first deployed (`revisionDeployedAt`,
   * argoApp.ts).
   */
  revisionDeployedAt?: string;
}

/**
 * Whole days elapsed since `iso`, rounded down. Undefined for a missing or
 * unparseable value — the payload comes verbatim from ArgoCD. Never negative:
 * a browser clock behind the cluster's would otherwise print « -1 j ».
 */
export function daysSince(
  iso: string | undefined,
  now: number,
): number | undefined {
  if (!iso) {
    return undefined;
  }
  const at = Date.parse(iso);
  if (Number.isNaN(at)) {
    return undefined;
  }
  return Math.max(0, Math.floor((now - at) / DAY_MS));
}

/** The age of the environment, in days since its Application was created. */
export function environmentAgeDays(
  timing: EnvironmentTiming,
  now: number,
): number | undefined {
  return daysSince(timing.createdAt, now);
}

/**
 * Days without a new deployment: since the current revision was first
 * deployed, or since the creation for an environment never deployed.
 *
 * An approximation of "nobody works on this branch any more": the last commit
 * date is not read (no request is added for it), but every push produces a new
 * revision that ArgoCD deploys, so the two only differ by the deployment time.
 */
export function idleDays(
  timing: EnvironmentTiming,
  now: number,
): number | undefined {
  return (
    daysSince(timing.revisionDeployedAt, now) ??
    daysSince(timing.createdAt, now)
  );
}

/**
 * Whether an idle time reaches {@link INACTIVE_AFTER_DAYS}, boundary
 * included.
 */
export function isInactive(idle: number | undefined): boolean {
  return idle !== undefined && idle >= INACTIVE_AFTER_DAYS;
}

/** Everything a branch label shows, computed once. */
export interface BranchActivity {
  ephemeral: boolean;
  /** Undefined without a creation date (e.g. a branch no longer listed). */
  ageDays?: number;
  idleDays?: number;
  /** Only ever true for an ephemeral branch. */
  inactive: boolean;
}

export function branchActivity(
  branch: string,
  timing: EnvironmentTiming | undefined,
  now: number,
): BranchActivity {
  const ephemeral = isEphemeralBranch(branch);
  if (!timing) {
    return { ephemeral, inactive: false };
  }
  const idle = idleDays(timing, now);
  return {
    ephemeral,
    ageDays: environmentAgeDays(timing, now),
    idleDays: idle,
    // A permanent branch is never "forgotten": it is not deleted with a
    // branch, so there is nothing to remind anyone of.
    inactive: ephemeral && isInactive(idle),
  };
}
